import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  MessageCircle,
  Loader2,
  User,
  Sparkles,
  Copy,
  Check,
  Send,
  ChevronRight,
  Smartphone,
  MessageSquare,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  ActionBackButton,
  LeadInfoCard,
  ActionPageHeader,
  ActionStatsGrid,
  ActionTipsList,
  SectionHeader,
  HistoryItemCard,
  EmptyState,
} from "../../components/admin/ai-actions";

const API = "/api";

export default function AITextPage() {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [textHistory, setTextHistory] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [currentText, setCurrentText] = useState(null);
  const [copied, setCopied] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState("sms");
  const [selectedTemplate, setSelectedTemplate] = useState("intro");
  const [conversation, setConversation] = useState([]);
  
  const token = localStorage.getItem("admin_token");

  const channels = [
    { id: "sms", label: "SMS", icon: Smartphone },
    { id: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  ];

  const templates = [
    { id: "intro", label: "Intro", description: "First touch outreach", icon: "👋" },
    { id: "follow_up", label: "Follow-Up", description: "After no reply", icon: "📅" },
    { id: "meeting", label: "Meeting", description: "Schedule a call", icon: "📆" },
    { id: "value_drop", label: "Value Drop", description: "Share insight", icon: "💡" },
  ];

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchLead();
    fetchTextHistory();
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLead(data);
      } else {
        toast.error("Lead not found");
        navigate(-1);
      }
    } catch (error) {
      toast.error("Failed to load lead");
    } finally {
      setLoading(false);
    }
  };

  const fetchTextHistory = async () => {
    try {
      const response = await fetch(`${API}/admin/leads/${leadId}/text-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTextHistory(data.text_history || []);
        const conv = (data.text_history || []).flatMap(text => {
          const messages = [];
          const messageContent = text.body || text.content;
          if (messageContent) {
            messages.push({
              type: "sent",
              text: messageContent,
              date: text.generated_at,
              status: text.status || "delivered"
            });
          }
          if (text.reply) {
            messages.push({
              type: "received",
              text: text.reply,
              date: text.reply_at
            });
          }
          return messages;
        });
        setConversation(conv);
      }
    } catch (error) {
      console.error("Failed to fetch text history:", error);
    }
  };

  const generateText = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API}/admin/crm/draft-text?lead_id=${leadId}&template=${selectedTemplate}&channel=${selectedChannel}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const newText = data.text_draft || `Hi ${lead?.first_name}! Quick intro about how we can help ${lead?.company_name || "your business"} save time and reduce costs...`;
        setCurrentText({
          body: newText,
          to: lead?.mobile_phone,
          generated_at: new Date().toISOString(),
          template: selectedTemplate
        });
        
        setConversation(prev => [...prev, {
          type: "sent",
          text: newText,
          date: new Date().toISOString(),
          status: "pending"
        }]);
        
        toast.success("SMS draft generated!");
        fetchTextHistory();
      } else {
        toast.error("Failed to generate SMS");
      }
    } catch (error) {
      toast.error("Network error - please try again");
    } finally {
      setGenerating(false);
    }
  };

  const copyText = () => {
    if (currentText) {
      navigator.clipboard.writeText(currentText.body);
      setCopied(true);
      toast.success("SMS copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const sendViaSMS = () => {
    if (currentText) {
      const phoneNumber = currentText.to || lead?.mobile_phone;
      window.open(`sms:${phoneNumber}?body=${encodeURIComponent(currentText.body)}`, '_self');
      toast.success("Opening SMS app...");
    }
  };

  const sendViaWhatsApp = () => {
    if (currentText) {
      const phoneNumber = (currentText.to || lead?.mobile_phone || "").replace(/[^0-9]/g, "");
      window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(currentText.body)}`, '_blank');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDraftedCount = () => textHistory.length;
  const getSentCount = () => textHistory.filter(t => t.status === "sent" || t.status === "delivered").length;
  const getRepliedCount = () => textHistory.filter(t => t.replied || t.reply).length;
  const getReplyRate = () => {
    const sent = getSentCount();
    if (sent === 0) return 0;
    return Math.round((getRepliedCount() / sent) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-400" />
      </div>
    );
  }

  const phoneNumber = lead?.mobile_phone || lead?.company_phone;

  // Stats for the grid
  const smsStats = [
    { value: getDraftedCount(), label: "Drafted", color: "text-white" },
    { value: getSentCount(), label: "Sent", color: "text-white" },
    { value: getRepliedCount(), label: "Replied", color: "text-white" },
    { value: `${getReplyRate()}%`, label: "Reply Rate", color: "text-green-400" },
  ];

  // Tips for SMS
  const smsTips = [
    "Keep under 160 chars for single SMS",
    "AI optimizes for engagement and response",
    "Use WhatsApp for international contacts",
    "Best send time: 10 AM-12 PM prospect time",
    "Personalize with first name for 40% more replies",
  ];

  return (
    <div className="min-h-screen bg-[#050505] p-6" data-testid="ai-text-page">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Lead Info & Controls */}
          <div className="col-span-3 space-y-5">
            {/* Back Button - Using shared component */}
            <ActionBackButton leadId={leadId} leadName={lead?.first_name} />

            {/* Lead Card - Using shared component */}
            <LeadInfoCard lead={lead} accentColor="green" />

            {/* Channel Selection */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-3">Channel</p>
              <div className="flex gap-2">
                {channels.map((channel) => {
                  const Icon = channel.icon;
                  return (
                    <button
                      key={channel.id}
                      onClick={() => setSelectedChannel(channel.id)}
                      data-testid={`channel-${channel.id}`}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl border transition-all flex-1 ${
                        selectedChannel === channel.id
                          ? "border-green-500/50 bg-green-500/10 text-green-400"
                          : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{channel.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Template Selection */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-3">Template</p>
              <div className="space-y-2">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    data-testid={`template-${template.id}`}
                    className={`w-full text-left p-3 rounded-xl border transition-all ${
                      selectedTemplate === template.id
                        ? "border-green-500/50 bg-green-500/10"
                        : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{template.icon}</span>
                      <div>
                        <span className={`font-medium text-sm ${selectedTemplate === template.id ? "text-green-400" : "text-white"}`}>
                          {template.label}
                        </span>
                        <p className="text-xs text-zinc-500">{template.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={generateText}
              disabled={generating || !phoneNumber}
              data-testid="generate-sms-btn"
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white py-5 rounded-xl text-sm font-medium"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Generate SMS
            </Button>

            {/* History Section */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase font-semibold tracking-widest mb-3">History</p>
              <div className="space-y-2 max-h-[220px] overflow-y-auto">
                {textHistory.length > 0 ? (
                  textHistory.map((text, idx) => (
                    <div 
                      key={idx}
                      onClick={() => setCurrentText(text)}
                      className="bg-zinc-900/60 rounded-xl p-3 border border-zinc-800/50 cursor-pointer hover:border-green-500/30 transition-all"
                      data-testid={`history-item-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs text-zinc-500">{formatDate(text.generated_at)}</span>
                        {(text.status === "sent" || text.status === "delivered") && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-green-500/20 text-green-400">
                            Delivered
                          </span>
                        )}
                        {(text.replied || text.reply) && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-blue-500/20 text-blue-400">
                            Replied
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-zinc-300 line-clamp-2">{text.body || text.content}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 bg-zinc-900/30 rounded-xl border border-zinc-800/30">
                    <p className="text-xs text-zinc-600">No messages yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Center Column - Phone Mockup */}
          <div className="col-span-5 flex justify-center">
            <div className="w-[300px]">
              {/* Phone Frame */}
              <div className="bg-[#1c1c1e] rounded-[40px] p-2.5 border-4 border-zinc-800 shadow-2xl">
                {/* Phone Screen */}
                <div className="bg-[#000000] rounded-[32px] overflow-hidden">
                  {/* Dynamic Island */}
                  <div className="flex justify-center pt-2 pb-1">
                    <div className="w-20 h-5 bg-zinc-900 rounded-full" />
                  </div>
                  
                  {/* Status Bar */}
                  <div className="flex items-center justify-between px-5 py-0.5 text-white text-[10px]">
                    <span className="font-medium">9:41</span>
                    <div className="flex items-center gap-1">
                      <span className="text-[8px]">...</span>
                      <span>100%</span>
                    </div>
                  </div>
                  
                  {/* Contact Header */}
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800/50">
                    <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{lead?.first_name} {lead?.last_name}</p>
                      <p className="text-[10px] text-zinc-500">{phoneNumber}</p>
                    </div>
                  </div>
                  
                  {/* Messages Area */}
                  <div className="h-[380px] px-3 py-4 overflow-y-auto space-y-3">
                    {conversation.length > 0 ? (
                      conversation.map((msg, idx) => (
                        <div key={idx}>
                          {msg.type === "sent" ? (
                            <div className="flex justify-end">
                              <div className="max-w-[85%]">
                                <div className="bg-green-500 text-white rounded-2xl rounded-br-sm px-3.5 py-2">
                                  <p className="text-[13px] leading-relaxed">{msg.text}</p>
                                </div>
                                <p className="text-[9px] text-zinc-600 text-right mt-1">
                                  {formatDate(msg.date)} · {msg.status === "delivered" ? "Delivered" : "Sent"}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <div className="flex justify-start">
                              <div className="max-w-[85%]">
                                <div className="bg-zinc-800 text-white rounded-2xl rounded-bl-sm px-3.5 py-2">
                                  <p className="text-[13px] leading-relaxed">{msg.text}</p>
                                </div>
                                <p className="text-[9px] text-zinc-600 mt-1">{formatDate(msg.date)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : currentText ? (
                      <div className="flex justify-end">
                        <div className="max-w-[85%]">
                          <div className="bg-green-500 text-white rounded-2xl rounded-br-sm px-3.5 py-2">
                            <p className="text-[13px] leading-relaxed">{currentText.body}</p>
                          </div>
                          <p className="text-[9px] text-zinc-600 text-right mt-1">Draft</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <p className="text-zinc-600 text-xs">Generate a message to preview</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Message Input */}
                  <div className="px-3 pb-4 pt-2 border-t border-zinc-800/50">
                    <div className="flex items-center gap-2 bg-zinc-900 rounded-full px-4 py-2">
                      <input 
                        type="text" 
                        placeholder="Message" 
                        className="flex-1 bg-transparent text-white text-sm outline-none placeholder-zinc-600"
                        readOnly
                      />
                      <button 
                        onClick={selectedChannel === "whatsapp" ? sendViaWhatsApp : sendViaSMS}
                        disabled={!currentText}
                        data-testid="send-btn"
                        className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center disabled:opacity-40 transition-opacity"
                      >
                        <Send className="w-3.5 h-3.5 text-white" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Actions below phone */}
              {currentText && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button
                    onClick={copyText}
                    variant="outline"
                    size="sm"
                    data-testid="copy-btn"
                    className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 text-xs"
                  >
                    {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                  <Button
                    onClick={sendViaSMS}
                    size="sm"
                    data-testid="send-sms-btn"
                    className="bg-green-500 hover:bg-green-600 text-white text-xs"
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Send SMS
                  </Button>
                  {selectedChannel === "whatsapp" && (
                    <Button
                      onClick={sendViaWhatsApp}
                      size="sm"
                      data-testid="send-whatsapp-btn"
                      className="bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs"
                    >
                      WhatsApp
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Header, Statistics & Tips */}
          <div className="col-span-4 space-y-6">
            {/* Page Header - Using shared component */}
            <ActionPageHeader 
              icon={MessageCircle}
              title="AI Text Composer"
              subtitle={`Draft SMS messages for ${lead?.first_name} ${lead?.last_name}`}
              gradientFrom="green-500"
              gradientTo="emerald-500"
            />

            {/* SMS Statistics - Using shared component */}
            <div>
              <SectionHeader title="SMS Statistics" />
              <ActionStatsGrid stats={smsStats} columns={2} />
            </div>

            {/* SMS Tips - Using shared component */}
            <div>
              <SectionHeader title="SMS Tips" />
              <ActionTipsList tips={smsTips} accentColor="green" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
